import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useContentRun, useSelectIdea } from "../hooks/useContent";
import { IdeaList } from "../components/ideas/IdeaList";
import { PipelineStatus } from "../components/pipeline/PipelineStatus";
import { Button } from "../components/ui/Button";

export function Ideas() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const { data: runData, isLoading, error } = useContentRun(runId);
  const selectIdea = useSelectIdea(runId || "");
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (error || !runData?.data) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center">
        <p className="mb-2 text-sm text-red-600">
          Failed to load content run.
        </p>
        <Button variant="secondary" size="sm" onClick={() => navigate("/dashboard")}>
          Back to dashboard
        </Button>
      </div>
    );
  }

  const run = runData.data;
  const agentStatus = run.agent_status;
  const ideas = run.ideas || [];

  // Determine if ideation is still processing
  const isIdeating =
    agentStatus.intake === "working" ||
    agentStatus.intake === "queued" ||
    agentStatus.ideation === "working" ||
    agentStatus.ideation === "queued";

  // Determine if any pipeline step has failed
  const hasFailed = Object.values(agentStatus).some((s) => s === "failed");

  // Check if pipeline is still processing (any step beyond ideation)
  const isProcessing = Object.values(agentStatus).some(
    (s) => s === "working" || s === "queued",
  );

  const handleSelect = (index: number) => {
    setSelectedIndex(index);
  };

  const handleContinue = async () => {
    if (selectedIndex === null) return;

    try {
      await selectIdea.mutateAsync({ idea_index: selectedIndex });
      navigate(`/review/${runId}`);
    } catch {
      // Error is available via selectIdea.error
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-xl font-bold text-slate-900">Choose an idea</h1>
        <p className="mt-1 text-sm text-slate-500">
          Pick the content direction that best fits your goals. We'll create
          platform-specific content from your selection.
        </p>
      </div>

      {/* Pipeline status - show when processing or failed */}
      {(isProcessing || hasFailed) && (
        <PipelineStatus agentStatus={agentStatus} />
      )}

      {/* Ideas list */}
      <IdeaList
        ideas={ideas}
        selectedIndex={selectedIndex}
        onSelect={handleSelect}
        loading={isIdeating && ideas.length === 0}
      />

      {/* Continue button */}
      {ideas.length > 0 && (
        <div className="sticky bottom-20 flex justify-end sm:bottom-4">
          <Button
            variant="primary"
            size="lg"
            disabled={selectedIndex === null}
            loading={selectIdea.isPending}
            onClick={handleContinue}
          >
            Continue with selected idea
          </Button>

          {selectIdea.isError && (
            <p className="mt-2 text-sm text-red-600">
              Failed to select idea. Please try again.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
